// New content is unread again; previous release and tour history stays in storage.
export const DATA_RELEASE_ID = 'data-workspace-2026-10';
export const DATA_TOUR_ID = 'analytics';

type LearningProgress = {
  completedTourIds: string[];
  readReleaseIds: string[];
};

const STORAGE_KEY_PREFIX = 'ics-ui:learning-center:';
const EMPTY_PROGRESS: LearningProgress = { completedTourIds: [], readReleaseIds: [] };

function getStorageKey(userId?: number) {
  return `${STORAGE_KEY_PREFIX}${userId ?? 'anonymous'}`;
}

export function getLearningProgress(userId?: number): LearningProgress {
  if (typeof window === 'undefined') return EMPTY_PROGRESS;

  try {
    const stored = window.localStorage.getItem(getStorageKey(userId));
    if (!stored) return EMPTY_PROGRESS;

    const parsed = JSON.parse(stored) as Partial<LearningProgress>;
    return {
      completedTourIds: Array.isArray(parsed.completedTourIds) ? parsed.completedTourIds : [],
      readReleaseIds: Array.isArray(parsed.readReleaseIds) ? parsed.readReleaseIds : [],
    };
  } catch {
    return EMPTY_PROGRESS;
  }
}

function updateLearningProgress(
  userId: number | undefined,
  update: (progress: LearningProgress) => LearningProgress
) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      getStorageKey(userId),
      JSON.stringify(update(getLearningProgress(userId)))
    );
  } catch {
    // Storage can be unavailable in some browsers and privacy modes.
  }
}

export function markReleaseRead(userId: number | undefined, releaseId: string) {
  updateLearningProgress(userId, (progress) => ({
    ...progress,
    readReleaseIds: Array.from(new Set([...progress.readReleaseIds, releaseId])),
  }));
}

export function markTourCompleted(userId: number | undefined, tourId: string) {
  updateLearningProgress(userId, (progress) => ({
    ...progress,
    completedTourIds: Array.from(new Set([...progress.completedTourIds, tourId])),
  }));
}

export function hasUnreadLearningContent(userId?: number) {
  return !getLearningProgress(userId).readReleaseIds.includes(DATA_RELEASE_ID);
}
